import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const useAuth = (redirectTo = '/') => {
     const navigate = useNavigate();
     const BASE_URL = process.env.REACT_APP_BASE_URL;
     const [profile, setProfile] = useState(null);
     const [isLoadingProfile, setIsLoadingProfile] = useState(false);
     const hasInitialized = useRef(false);

     // Get token function
     const getToken = useCallback(() => {
          return sessionStorage.getItem('token') || localStorage.getItem('token');
     }, []);

     // Check if user is authenticated
     const isAuthenticated = useCallback(() => {
          return !!getToken();
     }, [getToken]);

     // Get profile from session (not memoized to avoid stale data)
     const getProfileFromSession = () => {
          const profileData = sessionStorage.getItem('userProfile');
          return profileData ? JSON.parse(profileData) : null;
     };

     // Save profile to session (not memoized)
     const saveProfileToSession = (profileData) => {
          sessionStorage.setItem('userProfile', JSON.stringify(profileData));
     };

     // Fetch profile from API
     // ...existing code...
     const fetchProfile = useCallback(async (forceRefresh = false) => {
          // Check if profile already exists in session
          const cachedProfile = getProfileFromSession();
          if (cachedProfile && !forceRefresh) {
               setProfile(cachedProfile);
               return cachedProfile;
          }

          const token = getToken();
          if (!token) {
               console.warn('fetchProfile: no token found');
               return null;
          }

          try {
               setIsLoadingProfile(true);

               // safe build URL (avoid double slashes)
               const base = (BASE_URL || '').replace(/\/+$/, '');
               const url = base ? `${base}/user_profile_new` : '/user_profile_new';
               console.log('fetchProfile -> URL:', url);
               console.log('fetchProfile -> token present:', !!token);

               const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                         'Content-Type': 'application/json',
                         'Authorization': `Bearer ${token}`,
                    },
               });

               const text = await response.text();
               let result = null;
               try {
                    result = text ? JSON.parse(text) : null;
               } catch (parseErr) {
                    console.warn('fetchProfile: response not valid JSON', text);
               }

               console.log('fetchProfile -> status:', response.status, 'body:', result ?? text);

               if (response.ok && result && result.data) {
                    saveProfileToSession(result.data);
                    setProfile(result.data);
                    return result.data;
               } else {
                    // Helpful error message: include parsed JSON message or raw text
                    const msg = result?.message || result?.error || text || response.statusText || `Status ${response.status}`;
                    console.error('fetchProfile failed:', msg);
                    throw new Error(msg);
               }
          } catch (error) {
               console.error('Error fetching profile:', error);
               return null;
          } finally {
               setIsLoadingProfile(false);
          }
     }, [getToken, BASE_URL]);

     // Clear profile from session
     const clearProfile = useCallback(() => {
          sessionStorage.removeItem('userProfile');
          setProfile(null);
     }, []);

     // Auth fetch utility
     const authFetch = useCallback(async (url, options = {}) => {
          const token = getToken();

          if (!token) {
               throw new Error('No authentication token found');
          }

          const response = await fetch(url, {
               ...options,
               headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    ...options.headers,
               },
          });

          return response;
     }, [getToken]);

     // Logout function
     const logout = useCallback(() => {
          sessionStorage.removeItem('token');
          localStorage.removeItem('token');
          clearProfile();
          navigate(redirectTo);
     }, [navigate, redirectTo, clearProfile]);

     // Initialize profile on mount - only runs once
     useEffect(() => {
          if (hasInitialized.current) return;
          hasInitialized.current = true;

          const initProfile = async () => {
               // Check authentication
               if (!isAuthenticated()) {
                    navigate(redirectTo);
                    return;
               }

               // Try to load from session first
               const cachedProfile = getProfileFromSession();
               if (cachedProfile) {
                    console.log('Profile loaded from session:', cachedProfile);
                    setProfile(cachedProfile);
               } else {
                    // Fetch from API if not in session
                    console.log('Fetching profile from API...');
                    await fetchProfile(false);
               }
          };

          initProfile();
     }, [navigate, redirectTo, isAuthenticated, fetchProfile]);

     return {
          getToken,
          isAuthenticated,
          authFetch,
          profile,
          isLoadingProfile,
          fetchProfile,
          clearProfile,
          logout,
          getProfileFromSession,
     };
};

export default useAuth;

















// // src/hooks/useAuth.js
// import { useEffect, useCallback } from 'react';
// import { useNavigate } from 'react-router-dom';

// const useAuth = (redirectTo = '/') => {
//     const navigate = useNavigate();

//     // Get token function
//     const getToken = useCallback(() => {
//         return sessionStorage.getItem('token');
//     }, []);

//     // Check if user is authenticated
//     const isAuthenticated = useCallback(() => {
//         return !!getToken();
//     }, [getToken]);

//     // Auth fetch utility
//     const authFetch = useCallback(async (url, options = {}) => {
//         const token = getToken();
        
//         if (!token) {
//             throw new Error('No authentication token found');
//         }

//         const response = await fetch(url, {
//             ...options,
//             headers: {
//                 'Content-Type': 'application/json',
//                 'Authorization': `Bearer ${token}`,
//                 ...options.headers,
//             },
//         });

//         return response;
//     }, [getToken]);

//     // Redirect if not authenticated (existing functionality)
//     useEffect(() => {
//         if (!isAuthenticated()) {
//             navigate(redirectTo);
//         }
//     }, [navigate, redirectTo, isAuthenticated]);

//     return {
//         getToken,
//         isAuthenticated,
//         authFetch
//     };
// };

// export default useAuth;